import React from 'react';
import { ExternalLink, Link2Off } from 'lucide-react';
import { Button as ShadcnButton } from 'shadcn-ui';
import {
  ConnectionProfile,
  ExternalProjectLink,
  Project,
  PROJECT_LINKS_UPDATED,
  PROJECT_SYNCING_UPDATED,
  SetupStatus,
  useProjectsService,
} from 'nekst-api';
import {
  LoadingAnimation,
  PopupContext,
  Text,
  TextLink,
  usePopupManager,
} from 'ui-builder';
import { ShadowedCard } from 'features/nekst-widgets';
import { FubConnectionContext, useFubConnectionContextRequired } from '../../../Integrations';
import { useDataLoaderNew } from 'data-loader';
import CreateExternalProjectForm from './CreateExternalProjectForm';
import SetupNotCompletedPopup from './SetupNotCompletedPopup';
import useUnlinkProjectAction from './unlinkProjectAction';
import fubIcon from '../../../People/View/Connections/fub.png';

function FubIconBadge() {
  return (
    <div className="flex items-center justify-center border border-gray-200 rounded-lg size-10 shrink-0">
      <img
        src={fubIcon}
        alt="Follow Up Boss"
        width={32}
        height={32}
        className="shrink-0"
      />
    </div>
  );
}

function ShadcnUnlinkButton(props: {
  project: Project,
  link: ExternalProjectLink,
}) {
  const unlinkAction = useUnlinkProjectAction(props.project.id, props.link.id);

  return (
    <ShadcnButton
      variant="ghost"
      size="xs"
      className="text-gray-500 hover:text-rose-600"
      onClick={async () => {
        await unlinkAction.onSelected();
      }}
    >
      <Link2Off className="size-3.5 mr-1" />
      Unlink
    </ShadcnButton>
  );
}

function ShadcnExistingLinkCard(props: {
  project: Project,
  link: ExternalProjectLink,
}) {
  const statusDotClass = props.link.isConflicting
    ? 'bg-amber-500'
    : 'bg-emerald-500';

  const statusTitle = props.link.isConflicting
    ? 'Transaction syncing is currently disabled due to a conflict. Nekst couldn\'t determine how to sync two unsynced records.'
    : undefined;

  return (
    <ShadowedCard className="px-4 py-3">
      <div className="flex items-center gap-3">
        <FubIconBadge />
        <div className="min-w-0 flex-1">
          <span className="text-app-body font-medium text-gray-800">
            Follow Up Boss
          </span>
          <div
            className="flex items-center gap-1.5 mt-0.5"
            title={statusTitle}
          >
            <div className={`size-2 rounded-full shrink-0 ${statusDotClass}`} />
            <span className="text-app-xs text-gray-400">
              {props.link.isConflicting ? 'Syncing Conflict' : 'Deals Syncing'}
            </span>
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center gap-1 border-t border-gray-200 pt-3 mt-3">
        <ShadcnButton
          variant="ghost"
          size="xs"
          className="text-blue-600 hover:text-blue-700"
          asChild
        >
          <a
            href={props.link._externalProjectUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Follow Up Boss
            <ExternalLink className="size-3.5 ml-1" />
          </a>
        </ShadcnButton>
        <ShadcnUnlinkButton project={props.project} link={props.link} />
      </div>
    </ShadowedCard>
  );
}

function ShadcnNoLinksCard(props: {
  project: Project,
  profile: ConnectionProfile,
}) {
  const popupManager = usePopupManager();

  const handleCreateDeal = () => {
    if (props.profile.projectsSyncSetupStatuses[props.project.type] === SetupStatus.SETUP) {
      popupManager.openPopup(<CreateExternalProjectForm projectId={props.project.id} />);
    } else {
      popupManager.openPopup(
        <SetupNotCompletedPopup
          profile={props.profile}
          projectType={props.project.type}
        />,
      );
    }
  };

  return (
    <ShadowedCard className="px-4 py-3">
      <div className="flex items-center gap-3">
        <FubIconBadge />
        <div className="min-w-0 flex-1">
          <span className="text-app-body font-medium text-gray-800">
            Follow Up Boss
          </span>
        </div>
      </div>
      <div className="border-t border-gray-200 pt-3 mt-3">
        <ShadcnButton
          variant="default"
          size="sm"
          onClick={handleCreateDeal}
        >
          Create FUB Deal
        </ShadcnButton>
      </div>
    </ShadowedCard>
  );
}

function ShadcnNoConnectionView() {
  return (
    <Text>
      To integrate your contacts with other applications, see our current integration partners
      {' '}
      <TextLink href="/settings/integrations/fub">here</TextLink>.
    </Text>
  );
}

function ShadcnExternalLinksContent(props: {
  project: Project,
}) {
  const context = useFubConnectionContextRequired();

  if (!context.loaded) {
    return <LoadingAnimation />;
  } else if (context.profiles.length === 0) {
    return <ShadcnNoConnectionView />;
  } else if (props.project.externalLinks.length) {
    return (
      <div className="flex flex-col gap-3">
        {props.project.externalLinks.map((link) => (
          <ShadcnExistingLinkCard
            key={link.id}
            project={props.project}
            link={link}
          />
        ))}
      </div>
    );
  } else {
    return (
      <ShadcnNoLinksCard
        project={props.project}
        profile={context.profiles[0]}
      />
    );
  }
}

export function ShadcnExternalLinksList(props: {
  projectId: number,
}) {
  const projectService = useProjectsService();

  const { data: project } = useDataLoaderNew(
    async () => {
      return await projectService.getById(props.projectId);
    },
    {
      dependencies: [props.projectId],
      events: [PROJECT_SYNCING_UPDATED, PROJECT_LINKS_UPDATED],
    },
  );

  if (project) {
    return (
      <FubConnectionContext>
        <PopupContext>
          <ShadcnExternalLinksContent project={project} />
        </PopupContext>
      </FubConnectionContext>
    );
  } else {
    return <LoadingAnimation />;
  }
}
